import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { parse } from "date-fns";
import { isValidCreditCard } from "@/lib/helpers/credit-card-validation";
import { getPlatform } from "@/lib/utils";

const tokenizePaymentMethodSchema = z.object({
  name: z.string(),
  number: z.string().superRefine((value, ctx) => {
    if (value.startsWith("4")) {
      ctx.addIssue({
        code: "custom",
        error:
          "We apologize that we are currently unable to accept Visa, please try with a different card or PayNow for lower fees!",
      });
      return;
    }
    if (!isValidCreditCard(value)) {
      ctx.addIssue({
        code: "custom",
        error: "Must be a valid credit card number",
      });
    }
  }),
  cvc: z
    .string()
    .min(3, { error: "CVC must be at least 3 digits" })
    .max(4, { error: "CVC must be at least 4 digits" }),
  expDate: z
    .string()
    .length(7, { error: "must be valid format (MM/YYYY)" })
    .refine(
      (value) => {
        const [month, year] = value.split("/").map(Number);

        if (isNaN(month) || isNaN(year) || month < 1 || month > 12) {
          return false;
        }

        const expDate = parse(`01/${value}`, "dd/MM/yyyy", new Date());
        const currentDate = new Date();
        const beginningOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1,
        );

        return expDate >= beginningOfMonth;
      },
      { error: "must be a valid expiration date" },
    ),
});

// Infer the TypeScript type from the schema
export type TokenizePaymentMethodFormData = z.input<
  typeof tokenizePaymentMethodSchema
>;

export interface AddressFormReturn
  extends UseFormReturn<TokenizePaymentMethodFormData> {
  defaultValues: TokenizePaymentMethodFormData;
}

export const initialFormValues: TokenizePaymentMethodFormData = {
  name: "",
  number: "",
  cvc: "",
  expDate: "",
};

export const useTokenizePaymentMethodForm = (
  initialValues?: TokenizePaymentMethodFormData,
): AddressFormReturn => ({
  defaultValues: initialValues || initialFormValues,
  ...useForm<TokenizePaymentMethodFormData>({
    mode: getPlatform() === "native" ? "onSubmit" : "onChange",
    defaultValues: initialValues,
    resolver: zodResolver(tokenizePaymentMethodSchema),
  }),
});
